import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Accordion,
  AccordionItem,
  Input,
  Select,
  SelectItem,
  Textarea,
  useDisclosure,
  TimeInput
} from "@heroui/react";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";

const SplitMergeModal = ({ 
  isOpen, 
  onClose, 
  currentParagraph, 
  previousParagraph, 
  nextParagraph, 
  speakers,
  onSave 
}) => {
  const [previousContent, setPreviousContent] = useState({
    raw: previousParagraph?.raw_content || "",
    original: previousParagraph?.original_content || "",
    corrected: previousParagraph?.corrected_content || ""
  });
  const [currentContent, setCurrentContent] = useState({
    raw: currentParagraph?.raw_content || "",
    original: currentParagraph?.original_content || "",
    corrected: currentParagraph?.corrected_content || ""
  });
  const [nextContent, setNextContent] = useState({
    raw: nextParagraph?.raw_content || "",
    original: nextParagraph?.original_content || "",
    corrected: nextParagraph?.corrected_content || ""
  });

  // New speaker creation states
  const [isCreatingPreviousSpeaker, setIsCreatingPreviousSpeaker] = useState(false);
  const [isCreatingNextSpeaker, setIsCreatingNextSpeaker] = useState(false);
  const [newPreviousSpeaker, setNewPreviousSpeaker] = useState({
    timestamp: "",
    speaker: "",
    raw: "",
    original: "",
    corrected: ""
  });
  const [newNextSpeaker, setNewNextSpeaker] = useState({
    timestamp: "",
    speaker: "",
    raw: "",
    original: "",
    corrected: ""
  });

  const [expandedSections, setExpandedSections] = useState(new Set(["current"]));

  useEffect(() => {
    if (isOpen) {
      // Reset states when modal opens
      setPreviousContent({
        raw: previousParagraph?.raw_content || "",
        original: previousParagraph?.original_content || "",
        corrected: previousParagraph?.corrected_content || ""
      });
      setCurrentContent({
        raw: currentParagraph?.raw_content || "",
        original: currentParagraph?.original_content || "",
        corrected: currentParagraph?.corrected_content || ""
      });
      setNextContent({
        raw: nextParagraph?.raw_content || "",
        original: nextParagraph?.original_content || "",
        corrected: nextParagraph?.corrected_content || ""
      });
      setExpandedSections(new Set(["current"]));
      setIsCreatingPreviousSpeaker(false);
      setIsCreatingNextSpeaker(false);
    }
  }, [isOpen, currentParagraph, previousParagraph, nextParagraph]);

  const handleSectionToggle = (sectionKey) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    // Validate new speaker timestamps before proceeding
    const isValidTimestamp = (value) => {
      if (!value) return false;
      const match = value.match(/^([0-9]{2}):([0-5][0-9]):([0-5][0-9])$/);
      return !!match;
    };

    if (
      (isCreatingPreviousSpeaker && newPreviousSpeaker.timestamp && !isValidTimestamp(newPreviousSpeaker.timestamp)) ||
      (isCreatingNextSpeaker && newNextSpeaker.timestamp && !isValidTimestamp(newNextSpeaker.timestamp))
    ) {
      // Prevent save if any timestamp is invalid
      return;
    }
    const updates = [];
    const emptySections = [];
    
    // Check for empty sections and prepare updates
    if (previousParagraph && (!previousContent.raw.trim() && !previousContent.original.trim() && !previousContent.corrected.trim())) {
      // Previous section is empty - mark for deletion
      emptySections.push({ type: 'previous', paragraphId: previousParagraph.id });
    } else if (previousParagraph && (previousContent.original !== previousParagraph.original_content || 
             previousContent.corrected !== previousParagraph.corrected_content ||
             previousContent.raw !== (previousParagraph.raw_content || ""))) {
      // Previous section has changes
      updates.push({
        type: 'update',
        paragraphId: previousParagraph.id,
        raw_content: previousContent.raw,
        original_content: previousContent.original,
        corrected_content: previousContent.corrected
      });
    }

    // Current section updates
    if (currentContent.original !== currentParagraph.original_content || 
        currentContent.corrected !== currentParagraph.corrected_content ||
        currentContent.raw !== (currentParagraph.raw_content || "")) {
      updates.push({
        type: 'update',
        paragraphId: currentParagraph.id,
        raw_content: currentContent.raw,
        original_content: currentContent.original,
        corrected_content: currentContent.corrected
      });
    }

    if (nextParagraph && (!nextContent.raw.trim() && !nextContent.original.trim() && !nextContent.corrected.trim())) {
      // Next section is empty - mark for deletion
      emptySections.push({ type: 'next', paragraphId: nextParagraph.id });
    } else if (nextParagraph && (nextContent.original !== nextParagraph.original_content || 
             nextContent.corrected !== nextParagraph.corrected_content ||
             nextContent.raw !== (nextParagraph.raw_content || ""))) {
      // Next section has changes
      updates.push({
        type: 'update',
        paragraphId: nextParagraph.id,
        raw_content: nextContent.raw,
        original_content: nextContent.original,
        corrected_content: nextContent.corrected
      });
    }

    // Handle new speaker creation
    if (isCreatingPreviousSpeaker && newPreviousSpeaker.timestamp && newPreviousSpeaker.speaker) {
      updates.push({
        type: 'create',
        timestamp: newPreviousSpeaker.timestamp,
        speaker_name: newPreviousSpeaker.speaker,
        raw_content: newPreviousSpeaker.raw,
        original_content: newPreviousSpeaker.original,
        corrected_content: newPreviousSpeaker.corrected
      });
    }

    if (isCreatingNextSpeaker && newNextSpeaker.timestamp && newNextSpeaker.speaker) {
      updates.push({
        type: 'create',
        timestamp: newNextSpeaker.timestamp,
        speaker_name: newNextSpeaker.speaker,
        raw_content: newNextSpeaker.raw,
        original_content: newNextSpeaker.original,
        corrected_content: newNextSpeaker.corrected
      });
    }

    // Handle empty sections confirmation
    if (emptySections.length > 0) {
      const confirmMessage = emptySections.map(section => 
        `The ${section.type} speaker section is now empty. Do you want to delete it?`
      ).join('\n\n');
      
      if (window.confirm(confirmMessage)) {
        emptySections.forEach(section => {
          updates.push({ type: 'delete', paragraphId: section.paragraphId });
        });
      }
    }

    onSave(updates);
    onClose();
  };

  const handleNewSpeakerToggle = (section) => {
    if (section === 'previous') {
      setIsCreatingPreviousSpeaker(!isCreatingPreviousSpeaker);
      if (isCreatingPreviousSpeaker) {
        setNewPreviousSpeaker({ timestamp: "", speaker: "", raw: "", original: "", corrected: "" });
      }
    } else if (section === 'next') {
      setIsCreatingNextSpeaker(!isCreatingNextSpeaker);
      if (isCreatingNextSpeaker) {
        setNewNextSpeaker({ timestamp: "", speaker: "", raw: "", original: "", corrected: "" });
      }
    }
  };

  const renderSpeakerSection = (sectionKey, title, content, setContent, isCreating, newSpeaker, setNewSpeaker, onToggleNew) => {
    const isExpanded = expandedSections.has(sectionKey);
    const isValidTimestamp = (value) => {
      if (!value) return false;
      const match = value.match(/^([0-9]{2}):([0-5][0-9]):([0-5][0-9])$/);
      return !!match;
    };

    const toTimeObj = (value) => {
      if (!value || !isValidTimestamp(value)) return undefined;
      const [hh, mm, ss] = value.split(":").map(Number);
      return { hour: hh, minute: mm, second: ss };
    };

    const fromTimeObj = (time) => {
      if (!time || typeof time.hour !== "number" || typeof time.minute !== "number") return "";
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      const second = typeof time.second === "number" ? time.second : 0;
      return `${pad(time.hour)}:${pad(time.minute)}:${pad(second)}`;
    };
    
    return (
      <div className="border rounded-lg mb-4">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer bg-gray-50"
          onClick={() => handleSectionToggle(sectionKey)}
        >
          <h3 className="text-lg font-semibold">{title}</h3>
          <div className="flex items-center gap-2">
            {sectionKey !== 'current' && (
              <Button
                size="sm"
                variant="light"
                startContent={<Plus className="w-4 h-4" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleNew(sectionKey);
                }}
              >
                New speaker
              </Button>
            )}
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4">
            {isCreating ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <TimeInput
                    label="Timestamp"
                    granularity="second"
                    hourCycle={24}
                    value={toTimeObj(newSpeaker.timestamp)}
                    onChange={(time) =>
                      setNewSpeaker((prev) => ({ ...prev, timestamp: fromTimeObj(time) }))
                    }
                    className="max-w-xs"
                  />
                  <Select
                    label="Speaker"
                    placeholder="Select speaker"
                    selectedKeys={newSpeaker.speaker ? [newSpeaker.speaker] : []}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0];
                      setNewSpeaker(prev => ({ ...prev, speaker: selected }));
                    }}
                  >
                    {Array.isArray(speakers) &&
                      speakers.map((speaker) => (
                        <SelectItem key={speaker.key} value={speaker.key}>
                          {speaker.label}
                        </SelectItem>
                      ))}
                  </Select>
                </div>
                <Textarea
                  label="Raw content"
                  placeholder="Enter raw content..."
                  value={newSpeaker.raw}
                  onValueChange={(value) => setNewSpeaker(prev => ({ ...prev, raw: value }))}
                  minRows={3}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Textarea
                    label="Original content"
                    placeholder="Enter original content..."
                    value={newSpeaker.original}
                    onValueChange={(value) => setNewSpeaker(prev => ({ ...prev, original: value }))}
                    minRows={3}
                  />
                  <Textarea
                    label="Corrected content"
                    placeholder="Enter corrected content..."
                    value={newSpeaker.corrected}
                    onValueChange={(value) => setNewSpeaker(prev => ({ ...prev, corrected: value }))}
                    minRows={3}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Textarea
                  label="Raw content"
                  value={content.raw}
                  onValueChange={(value) => setContent(prev => ({ ...prev, raw: value }))}
                  minRows={3}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Textarea
                    label="Original content"
                    value={content.original}
                    onValueChange={(value) => setContent(prev => ({ ...prev, original: value }))}
                    minRows={3}
                  />
                  <Textarea
                    label="Corrected content"
                    value={content.corrected}
                    onValueChange={(value) => setContent(prev => ({ ...prev, corrected: value }))}
                    minRows={3}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold">Split/Merge Speaker Sections</h2>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            {renderSpeakerSection(
              'previous',
              'Previous Speaker Section',
              previousContent,
              setPreviousContent,
              isCreatingPreviousSpeaker,
              newPreviousSpeaker,
              setNewPreviousSpeaker,
              handleNewSpeakerToggle
            )}
            
            {renderSpeakerSection(
              'current',
              'Current Speaker Section',
              currentContent,
              setCurrentContent,
              false,
              null,
              null,
              null
            )}
            
            {renderSpeakerSection(
              'next',
              'Next Speaker Section',
              nextContent,
              setNextContent,
              isCreatingNextSpeaker,
              newNextSpeaker,
              setNewNextSpeaker,
              handleNewSpeakerToggle
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="bordered" radius="full" onPress={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={
              (isCreatingPreviousSpeaker && newPreviousSpeaker.timestamp && !/^([0-9]{2}):([0-5][0-9]):([0-5][0-9])$/.test(newPreviousSpeaker.timestamp)) ||
              (isCreatingNextSpeaker && newNextSpeaker.timestamp && !/^([0-9]{2}):([0-5][0-9]):([0-5][0-9])$/.test(newNextSpeaker.timestamp))
            }
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SplitMergeModal;
